import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

import { API_URL } from "@/utils/apiConfig";

const POLL_MS = 2500;
const MAX_ATTEMPTS = 24;

function formatAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return amount.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}

export default function TablePaymentThanksPage() {
  const [searchParams] = useSearchParams();
  const context = useMemo(() => ({
    restaurant_id: searchParams.get("restaurant_id") || "",
    table_id: searchParams.get("table_id") || "",
    session_id: searchParams.get("session_id") || "",
    request_id: searchParams.get("request_id") || "",
  }), [searchParams]);
  const [payment, setPayment] = useState({ status: "confirming" });
  const [delayed, setDelayed] = useState(false);
  const contextReady = Object.values(context).every(Boolean);

  useEffect(() => {
    if (!contextReady) {
      setDelayed(true);
      return undefined;
    }

    let stopped = false;
    let timer;
    let attempts = 0;

    const check = async () => {
      attempts += 1;
      try {
        const response = await axios.get(
          `${API_URL}/api/public/table-assist/payment-status`,
          { params: context },
        );
        if (stopped) return;
        setPayment(response.data || { status: "confirming" });
        if (response.data?.status === "paid") return;
      } catch (_) {
        // A confirmação financeira continua no servidor; o cliente não deve pagar novamente.
      }

      if (attempts >= MAX_ATTEMPTS) {
        if (!stopped) setDelayed(true);
        return;
      }
      timer = window.setTimeout(check, POLL_MS);
    };

    check();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [contextReady, context]);

  const paid = payment?.status === "paid";

  return (
    <main className="min-h-screen bg-[#faf9f6] flex items-center justify-center px-5 py-10">
      <section className="w-full max-w-md rounded-3xl bg-white shadow-xl border border-gray-100 px-6 py-10 text-center">
        {paid ? (
          <>
            <div className="mx-auto mb-5 h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-3xl font-black"
              aria-hidden="true">✓</div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
              Pago confirmado
            </p>
            <h1 className="mt-2 text-3xl font-black text-gray-900">¡Gracias por tu visita!</h1>
            {payment.amount != null && (
              <p className="mt-3 text-xl font-bold text-gray-700">{formatAmount(payment.amount)}</p>
            )}
            {payment.review_url && (
              <a href={payment.review_url} target="_blank" rel="noopener noreferrer"
                className="mt-8 h-14 w-full rounded-2xl bg-red-600 text-white font-black flex items-center justify-center"
                data-testid="table-payment-review">
                VALORAR TU EXPERIENCIA
              </a>
            )}
          </>
        ) : (
          <>
            <div className="mx-auto mb-5 h-12 w-12 rounded-full border-4 border-gray-200 border-t-red-600 animate-spin"
              aria-hidden="true" />
            <h1 className="text-2xl font-black text-gray-900">
              {delayed ? "Estamos confirmando tu pago" : "Confirmando tu pago…"}
            </h1>
            <p className="mt-3 text-base text-gray-600">
              {delayed
                ? "El pago ya fue enviado. No vuelvas a pagar; el restaurante recibirá la confirmación."
                : "No cierres esta página. No realices otro pago."}
            </p>
          </>
        )}
      </section>
    </main>
  );
}
